import React, { useCallback, useMemo, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	ButtonBase,
	Chip,
	FormControl,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ImageIcon from '@mui/icons-material/Image'
import SendIcon from '@mui/icons-material/Send'
import { mainBlue } from '../../../utils/color'
import { transformDetailWithPhone } from '../../../utils/transformDetail'
import {
	accountTypeForOrder,
	accountTypeLabel,
	formatMoney,
	isTransferredBankOrder,
	isTransferredOrder,
	orderAssignmentRowSx,
	orderCode,
	orderContactText,
	orderItemsText,
	orderUsesDefaultCashflowChannel,
	paymentLabel,
	resolveImageUrl,
	sectionSx,
	toId,
} from '../cashflowUtils'

const amountValue = (value) => Number(value) || 0
// Tổng tiền đơn đã gồm phí ship nên không cộng shippingTotal vào nữa
const getOrderTotal = (order) => amountValue(order?.totalPrice)
const getOrderId = (order) => String(toId(order))
const getIncomeLinkedOrderIds = (income) => (income?.orders || []).map((order) => String(toId(order))).filter(Boolean)
const getIncomeOffsetAmount = (income) => amountValue(income?.appliedAmount ?? income?.amount)
const getOrderPhotos = (order) => (Array.isArray(order?.photoAttach) ? order.photoAttach.filter(Boolean) : [])
const headerChipSx = {
	height: 32,
	borderRadius: '10px',
	maxWidth: '100%',
	minWidth: 0,
	px: 0.5,
	'& .MuiChip-label': {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
}
const filterControlSx = {
	flex: { xs: '1 1 100%', sm: '0 1 190px' },
	minWidth: { xs: 0, sm: 170 },
	maxWidth: '100%',
}
const selectMenuProps = {
	PaperProps: {
		sx: {
			maxWidth: 'calc(100vw - 24px)',
		},
	},
}
const receiverFilterValues = {
	unassigned: '__unassigned__',
	cashDefault: '__cash_default__',
	appDefault: '__app_default__',
	offsetCovered: '__offset_covered__',
	confirmedOutgoing: '__confirmed_outgoing__',
}
const accountFilterValue = (accountId) => `account:${accountId}`
const paymentFilterValueForOrder = (order) => {
	if (order?.app) return 'app'
	return order?.payment === 'transfer' ? 'transfer' : 'cash'
}
const receiverAccountFilterValueForOrder = (order, confirmedOutgoingPayment, remainingReceivable) => {
	if (confirmedOutgoingPayment) return receiverFilterValues.confirmedOutgoing
	const accountType = accountTypeForOrder(order)
	if (accountType === 'cash') return receiverFilterValues.cashDefault
	if (accountType === 'app') return receiverFilterValues.appDefault
	const assignedAccountId = toId(order?.cashflowBankAccount)
	if (assignedAccountId) return accountFilterValue(assignedAccountId)
	if (remainingReceivable === 0) return receiverFilterValues.offsetCovered
	return receiverFilterValues.unassigned
}
const addAccountFilterOption = (optionsById, account) => {
	const accountId = toId(account)
	if (!accountId || optionsById.has(accountId) || typeof account !== 'object') return
	optionsById.set(accountId, {
		value: accountFilterValue(accountId),
		label: account.name || accountTypeLabel(account),
	})
}

export default function OrderAccountAssignment({
	assignOrderAccount,
	bankAccounts = [],
	canManageExpense,
	classificationOrders,
	confirmedOutgoingPaymentByOrderId = {},
	getAccountsForOrder,
	incomeOffsetCandidates = [],
	incomeOrders,
	linkOrderIncomeOffset,
	openPaymentRequest,
	prepaidOffsetByOrder = {},
	setImageViewer = () => {},
	unclassifiedIncomeOrderCount,
	unlinkOrderIncomeOffset,
}) {
	const [accountStatusFilter, setAccountStatusFilter] = useState('all')
	const [paymentFilter, setPaymentFilter] = useState('all')
	const [receiverAccountFilter, setReceiverAccountFilter] = useState('all')
	const getConfirmedOutgoingPayment = useCallback(
		(order) => confirmedOutgoingPaymentByOrderId[toId(order)] || null,
		[confirmedOutgoingPaymentByOrderId],
	)
	const getRemainingReceivable = useCallback(
		(order) => Math.max(getOrderTotal(order) - amountValue(prepaidOffsetByOrder[getOrderId(order)]?.amount), 0),
		[prepaidOffsetByOrder],
	)
	const assignedIncomeOrderCount = classificationOrders.filter(
		(order) =>
			!getConfirmedOutgoingPayment(order) &&
			(orderUsesDefaultCashflowChannel(order) ||
				Boolean(toId(order.cashflowBankAccount)) ||
				getRemainingReceivable(order) === 0),
	).length
	const confirmedOutgoingOrderCount = classificationOrders.filter((order) => getConfirmedOutgoingPayment(order)).length
	const receiverAccountOptions = useMemo(() => {
		const visibleSpecialOptions = {
			unassigned: false,
			cashDefault: false,
			appDefault: false,
			offsetCovered: false,
			confirmedOutgoing: false,
		}
		const accountOptionsById = new Map()
		bankAccounts
			.filter((account) => (account.accountType || 'bank') === 'bank')
			.forEach((account) => addAccountFilterOption(accountOptionsById, account))
		classificationOrders.forEach((order) => {
			const confirmedOutgoingPayment = getConfirmedOutgoingPayment(order)
			const remainingReceivable = getRemainingReceivable(order)
			const receiverValue = receiverAccountFilterValueForOrder(order, confirmedOutgoingPayment, remainingReceivable)
			if (receiverValue === receiverFilterValues.unassigned) visibleSpecialOptions.unassigned = true
			if (receiverValue === receiverFilterValues.cashDefault) visibleSpecialOptions.cashDefault = true
			if (receiverValue === receiverFilterValues.appDefault) visibleSpecialOptions.appDefault = true
			if (receiverValue === receiverFilterValues.offsetCovered) visibleSpecialOptions.offsetCovered = true
			if (receiverValue === receiverFilterValues.confirmedOutgoing) visibleSpecialOptions.confirmedOutgoing = true
			addAccountFilterOption(accountOptionsById, order.cashflowBankAccount)
			getAccountsForOrder(order).forEach((account) => addAccountFilterOption(accountOptionsById, account))
		})
		return [
			...(visibleSpecialOptions.unassigned
				? [{ value: receiverFilterValues.unassigned, label: 'Chưa gán tài khoản' }]
				: []),
			...(visibleSpecialOptions.cashDefault
				? [{ value: receiverFilterValues.cashDefault, label: 'Tiền mặt mặc định' }]
				: []),
			...(visibleSpecialOptions.appDefault
				? [{ value: receiverFilterValues.appDefault, label: 'App mặc định' }]
				: []),
			...Array.from(accountOptionsById.values()),
			...(visibleSpecialOptions.offsetCovered
				? [{ value: receiverFilterValues.offsetCovered, label: 'Đã bù đủ' }]
				: []),
			...(visibleSpecialOptions.confirmedOutgoing
				? [{ value: receiverFilterValues.confirmedOutgoing, label: 'CH chuyển đã thu' }]
				: []),
		]
	}, [bankAccounts, classificationOrders, getAccountsForOrder, getConfirmedOutgoingPayment, getRemainingReceivable])
	const filteredClassificationOrders = useMemo(
		() =>
			classificationOrders.filter((order) => {
				const confirmedOutgoingPayment = getConfirmedOutgoingPayment(order)
				const remainingReceivable = getRemainingReceivable(order)
				const hasAssignedAccount =
					!confirmedOutgoingPayment &&
					(orderUsesDefaultCashflowChannel(order) ||
						Boolean(toId(order.cashflowBankAccount)) ||
						remainingReceivable === 0)
				if (accountStatusFilter === 'assigned' && !hasAssignedAccount) return false
				if (accountStatusFilter === 'unassigned' && (confirmedOutgoingPayment || hasAssignedAccount)) return false
				if (accountStatusFilter === 'confirmed-outgoing' && !confirmedOutgoingPayment) return false
				if (paymentFilter !== 'all' && paymentFilterValueForOrder(order) !== paymentFilter) return false
				if (
					receiverAccountFilter !== 'all' &&
					receiverAccountFilterValueForOrder(order, confirmedOutgoingPayment, remainingReceivable) !== receiverAccountFilter
				) {
					return false
				}
				return true
			}),
		[
			classificationOrders,
			accountStatusFilter,
			getConfirmedOutgoingPayment,
			getRemainingReceivable,
			paymentFilter,
			receiverAccountFilter,
		],
	)

	return (
		<Box sx={{ width: '100%' }}>
			<Paper elevation={0} sx={sectionSx}>
				<Stack
					direction={{ xs: 'column', md: 'row' }}
					spacing={1.5}
					justifyContent='space-between'
					alignItems={{ xs: 'flex-start', md: 'center' }}
					sx={{ mb: 2 }}>
					<Box>
						<Typography sx={{ fontWeight: 900, fontSize: 18 }}>Gán tài khoản nhận cho đơn</Typography>
						<Typography sx={{ color: '#637381', fontSize: 13, mt: 0.25 }}>
							Đơn nhận từ cửa hàng khác được đánh dấu theo nơi gửi. Nếu tiền vào cửa hàng hiện tại thì gán tài khoản nhận và
							vẫn tính vào báo cáo; chỉ gửi xác nhận liên CH khi cửa hàng chuyển đang giữ tiền.
						</Typography>
					</Box>
					<Stack
						direction='row'
						spacing={1}
						useFlexGap
						flexWrap='wrap'
						alignItems='center'
						justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
						sx={{
							width: { xs: '100%', md: 'auto' },
							maxWidth: '100%',
							minWidth: 0,
						}}>
						<Chip
							color={unclassifiedIncomeOrderCount > 0 ? 'warning' : 'success'}
							label={`${unclassifiedIncomeOrderCount} chưa gán / ${assignedIncomeOrderCount} đã xử lý`}
							sx={headerChipSx}
						/>
						{confirmedOutgoingOrderCount > 0 && (
							<Chip
								color='info'
								label={`${confirmedOutgoingOrderCount} CH chuyển đã thu`}
								sx={headerChipSx}
							/>
						)}
						<FormControl
							size='small'
							sx={filterControlSx}>
							<Select
								value={accountStatusFilter}
								onChange={(event) => setAccountStatusFilter(event.target.value)}
								MenuProps={selectMenuProps}
								inputProps={{ 'aria-label': 'Lọc trạng thái' }}
								sx={{ background: '#fff', fontWeight: 800 }}>
								<MenuItem value='all'>Tất cả trạng thái</MenuItem>
								<MenuItem value='unassigned'>Chưa gán</MenuItem>
								<MenuItem value='assigned'>Đã xử lý</MenuItem>
								<MenuItem value='confirmed-outgoing'>CH chuyển đã thu</MenuItem>
							</Select>
						</FormControl>
						<FormControl size='small' sx={filterControlSx}>
							<Select
								value={paymentFilter}
								onChange={(event) => setPaymentFilter(event.target.value)}
								MenuProps={selectMenuProps}
								inputProps={{ 'aria-label': 'Lọc thanh toán' }}
								sx={{ background: '#fff', fontWeight: 800 }}>
								<MenuItem value='all'>Tất cả thanh toán</MenuItem>
								<MenuItem value='cash'>Tiền mặt</MenuItem>
								<MenuItem value='transfer'>Chuyển khoản</MenuItem>
								<MenuItem value='app'>App</MenuItem>
							</Select>
						</FormControl>
						<FormControl
							size='small'
							sx={{
								...filterControlSx,
								flex: { xs: '1 1 100%', sm: '0 1 230px' },
								minWidth: { xs: 0, sm: 210 },
							}}>
							<Select
								value={receiverAccountFilter}
								onChange={(event) => setReceiverAccountFilter(event.target.value)}
								MenuProps={selectMenuProps}
								inputProps={{ 'aria-label': 'Lọc tài khoản nhận' }}
								sx={{ background: '#fff', fontWeight: 800 }}>
								<MenuItem value='all'>Tất cả tài khoản nhận</MenuItem>
								{receiverAccountOptions.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
				</Stack>
				{incomeOrders.length === 0 ? (
					<Alert severity='info'>Chưa có đơn hoàn thành trong ngày.</Alert>
				) : (
					<Box
						className='cashflow-table-shell cashflow-scroll'
						sx={{
							height: {
								xs: 'min(72dvh, 620px)',
								md: 'clamp(540px, calc(100dvh - 305px), 820px)',
							},
							minHeight: { xs: 420, md: 540 },
						}}>
						<Table size='small' stickyHeader sx={{ minWidth: 1310 }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ minWidth: 470 }}>Đơn / thông tin</TableCell>
									<TableCell sx={{ width: 128 }}>Thanh toán</TableCell>
									<TableCell sx={{ width: 160 }}>Trạng thái nhận</TableCell>
									<TableCell align='right' sx={{ width: 132 }}>
										Tổng tiền
									</TableCell>
									<TableCell align='right' sx={{ width: 120 }}>
										Trong đó ship
									</TableCell>
									<TableCell sx={{ width: 300 }}>Tài khoản nhận</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredClassificationOrders.map((order, index) => {
									const accounts = getAccountsForOrder(order)
									const confirmedOutgoingPayment = getConfirmedOutgoingPayment(order)
									const lockedByConfirmedPayment = Boolean(confirmedOutgoingPayment)
									const accountType = accountTypeForOrder(order)
									const usesDefaultChannel = orderUsesDefaultCashflowChannel(order)
									const orderId = getOrderId(order)
									const prepaidOffset = prepaidOffsetByOrder[orderId] || {}
									const prepaidOffsetAmount = amountValue(prepaidOffset.amount)
									const remainingReceivable = getRemainingReceivable(order)
									const offsetFullyCovered = prepaidOffsetAmount > 0 && remainingReceivable === 0
									const linkedIncomeOffsets = incomeOffsetCandidates.filter((income) =>
										getIncomeLinkedOrderIds(income).includes(orderId),
									)
									const availableIncomeOffsets = incomeOffsetCandidates.filter(
										(income) => getIncomeLinkedOrderIds(income).length === 0,
									)
									const selectedAccountId = lockedByConfirmedPayment ? '' : toId(order.cashflowBankAccount)
									const needsAccount =
										!lockedByConfirmedPayment && !usesDefaultChannel && !selectedAccountId && remainingReceivable > 0
									const contactText = orderContactText(order)
									const itemsText = orderItemsText(order)
									const quantityText = Number(order.quantity) > 0 ? `${order.quantity}kg` : ''
									const noteText = order.customerNote || order.detail
									const transferredOrder = isTransferredOrder(order)
									const transferredBankOrder = isTransferredBankOrder(order)
									const fromStoreName = order.transferStore?.fromStore?.name
									const photoUrls = getOrderPhotos(order)
									return (
										<TableRow key={order._id} sx={orderAssignmentRowSx(order, needsAccount, index)}>
											<TableCell sx={{ minWidth: 470 }}>
												<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
													<Typography sx={{ fontWeight: 900, fontSize: 16 }}>{orderCode(order)}</Typography>
													{transferredOrder && (
														<Chip size='small' color='warning' label='Đơn nhận' sx={{ height: 22 }} />
													)}
													{quantityText && <Chip size='small' label={quantityText} sx={{ height: 22 }} />}
													{order.app && <Chip size='small' color='info' label='App' sx={{ height: 22 }} />}
													{order.quickPurchase && (
														<Chip size='small' color='error' label='Mua nhanh' sx={{ height: 22 }} />
													)}
													{order.edited?.value && <Chip size='small' label='Đã sửa' sx={{ height: 22 }} />}
												</Stack>
												{itemsText && (
													<Typography sx={{ mt: 0.5, fontSize: 12, color: '#263238', fontWeight: 700 }}>
														{itemsText}
													</Typography>
												)}
												{order.tag?.length > 0 && (
													<Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{ mt: 0.5 }}>
														{order.tag.map((tag) => (
															<Chip
																key={`${order._id}-${tag.name}`}
																size='small'
																label={tag.name}
																sx={{
																	height: 22,
																	background: tag.color || '#ECEFF1',
																	color: '#fff',
																	fontWeight: 700,
																}}
															/>
														))}
													</Stack>
												)}
												{contactText && <Typography sx={{ fontSize: 12, color: '#637381' }}>{contactText}</Typography>}
												{transferredOrder && (
													<Typography sx={{ mt: 0.5, fontSize: 12, color: '#B76E00', fontWeight: 800 }}>
														Nhận từ {fromStoreName || 'cửa hàng khác'} ·{' '}
														{lockedByConfirmedPayment
															? 'CH chuyển đã xác nhận thu tiền'
															: transferredBankOrder
																? 'chuyển khoản, kiểm tra nơi tiền vào'
																: 'tiền mặt nhận tại cửa hàng này vẫn tính báo cáo'}
													</Typography>
												)}
												{photoUrls.length > 0 && (
													<Stack
														direction='row'
														spacing={0.75}
														flexWrap='wrap'
														useFlexGap
														alignItems='center'
														sx={{ mt: 0.75 }}>
														<Chip
															size='small'
															icon={<ImageIcon />}
															label={`${photoUrls.length} ảnh đính kèm`}
															sx={{ height: 26, fontWeight: 800, borderRadius: '8px' }}
														/>
														{photoUrls.map((photo, photoIndex) => {
															const photoUrl = resolveImageUrl(photo)
															const title = `${orderCode(order)} - ảnh đính kèm ${photoIndex + 1}`
															return (
																<Tooltip key={`${order._id}-photo-${photoIndex}`} title='Xem ảnh đính kèm'>
																	<ButtonBase
																		aria-label={title}
																		onClick={(event) => {
																			event.stopPropagation()
																			setImageViewer({ open: true, url: photoUrl, title })
																		}}
																		sx={{
																			width: 44,
																			height: 44,
																			borderRadius: '8px',
																			overflow: 'hidden',
																			border: '1px solid #DDE7DF',
																			background: '#F8FBF9',
																			flex: '0 0 auto',
																		}}>
																		<Box
																			component='img'
																			loading='lazy'
																			src={photoUrl}
																			alt={title}
																			sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
																		/>
																	</ButtonBase>
																</Tooltip>
															)
														})}
													</Stack>
												)}
												{noteText && (
													<Typography
														component='div'
														sx={{
															mt: 0.5,
															color: '#455A64',
															fontSize: 12,
															lineHeight: 1.45,
															display: '-webkit-box',
															WebkitLineClamp: 2,
															WebkitBoxOrient: 'vertical',
															overflow: 'hidden',
															'& a': { color: mainBlue, fontWeight: 700 },
														}}
														dangerouslySetInnerHTML={{ __html: transformDetailWithPhone(noteText) }}
													/>
												)}
											</TableCell>
											<TableCell>
												<Typography sx={{ fontWeight: 800, color: '#51665A' }}>
													{paymentLabel(order)}
												</Typography>
												{transferredOrder && (
													<Typography sx={{ mt: 0.25, color: '#637381', fontSize: 12, lineHeight: 1.35 }}>
														{lockedByConfirmedPayment
															? 'Không gán TK nhận tại cửa hàng này'
															: transferredBankOrder
																? 'Gán nếu tiền vào cửa hàng này'
																: 'Ghi nhận mặc định nếu cửa hàng này giữ tiền'}
													</Typography>
												)}
											</TableCell>
											<TableCell>
												<Chip
													size='small'
													color={lockedByConfirmedPayment ? 'info' : needsAccount ? 'warning' : 'success'}
													label={
														lockedByConfirmedPayment
															? 'CH chuyển đã thu'
															: needsAccount
																? 'Cần gán TK nhận'
																: usesDefaultChannel
																	? `${accountTypeLabel(accountType)} mặc định`
																	: offsetFullyCovered
																	? 'Đã bù đủ'
																	: 'Đã gán TK nhận'
													}
													sx={{ borderRadius: '8px', fontWeight: 800 }}
												/>
											</TableCell>
											<TableCell align='right' sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
												{formatMoney(order.totalPrice)}
											</TableCell>
											<TableCell align='right' sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
												{formatMoney(order.shippingTotal)}
											</TableCell>
											<TableCell sx={{ minWidth: 300 }}>
												{usesDefaultChannel && !lockedByConfirmedPayment ? (
													<Box
														sx={{
															p: 1,
															border: '1px solid #DDE7DF',
															borderRadius: '10px',
															background: '#F8FBF9',
														}}>
														<Chip
															size='small'
															color='success'
															label={`${accountTypeLabel(accountType)} mặc định`}
															sx={{ borderRadius: '8px', fontWeight: 800 }}
														/>
														<Typography sx={{ mt: 0.75, color: '#51665A', fontSize: 12, fontWeight: 700 }}>
															Không cần gán tài khoản riêng cho đơn {paymentLabel(order)}.
														</Typography>
													</Box>
												) : (
													<FormControl fullWidth size='small' error={needsAccount}>
														<Select
															displayEmpty
															value={selectedAccountId}
															disabled={!canManageExpense || lockedByConfirmedPayment || offsetFullyCovered}
															sx={{ background: '#fff', fontWeight: 700 }}
															onChange={(event) => assignOrderAccount(order, event.target.value)}>
															<MenuItem value=''>
																{lockedByConfirmedPayment
																	? 'Không gán tại cửa hàng này'
																	: offsetFullyCovered
																		? 'Đã bù đủ, không cần gán'
																		: 'Chưa gán tài khoản'}
															</MenuItem>
															{accounts.length === 0 && (
																<MenuItem value='' disabled>
																	Chưa có tài khoản phù hợp
																</MenuItem>
															)}
															{accounts.map((account) => (
																<MenuItem key={account._id} value={account._id}>
																	{account.name}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												)}
												<Typography
													sx={{
														mt: 0.5,
														color: remainingReceivable > 0 ? '#51665A' : '#1B5E20',
														fontSize: 12,
														fontWeight: 800,
													}}>
													Còn cần nhận: {formatMoney(remainingReceivable)}
												</Typography>
												{linkedIncomeOffsets.length > 0 && (
													<Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{ mt: 0.75 }}>
														{linkedIncomeOffsets.map((income) => (
															<Chip
																key={income._id}
																size='small'
																color='success'
																label={`${income.content} - ${formatMoney(getIncomeOffsetAmount(income))}`}
																onDelete={
																	canManageExpense && !lockedByConfirmedPayment
																		? () => unlinkOrderIncomeOffset?.(order, income._id)
																		: undefined
																}
																deleteIcon={<CloseIcon />}
																sx={{ maxWidth: '100%', borderRadius: '8px', fontWeight: 700 }}
															/>
														))}
													</Stack>
												)}
												{availableIncomeOffsets.length > 0 && !lockedByConfirmedPayment && (
													<FormControl fullWidth size='small' sx={{ mt: 1 }}>
														<Select
															displayEmpty
															value=''
															disabled={!canManageExpense || remainingReceivable <= 0}
															sx={{ background: '#fff', fontSize: 13 }}
															onChange={(event) => linkOrderIncomeOffset?.(order, event.target.value)}>
															<MenuItem value=''>Lấy từ khoản thu đã có</MenuItem>
															{availableIncomeOffsets.map((income) => (
																<MenuItem key={income._id} value={income._id}>
																	{income.content} - {formatMoney(income.amount)} - {income.reportDate}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												)}
												{lockedByConfirmedPayment && (
													<Typography sx={{ mt: 0.5, color: '#1976D2', fontSize: 12, fontWeight: 700 }}>
														Cửa hàng chuyển đã xác nhận thu tiền, không gán TK nhận ở đây.
													</Typography>
												)}
												{needsAccount && (
													<Typography sx={{ mt: 0.5, color: '#B76E00', fontSize: 12, fontWeight: 700 }}>
														Chọn tài khoản nhận tiền
													</Typography>
												)}
												{transferredOrder && !lockedByConfirmedPayment && (
													<Button
														size='small'
														variant='outlined'
														startIcon={<SendIcon />}
														disabled={!canManageExpense}
														sx={{ mt: 1, fontWeight: 800 }}
														onClick={() => openPaymentRequest?.(order)}>
														Yêu cầu CH chuyển xác nhận
													</Button>
												)}
											</TableCell>
										</TableRow>
									)
								})}
								{filteredClassificationOrders.length === 0 && (
									<TableRow>
										<TableCell colSpan={6} sx={{ py: 4, textAlign: 'center !important', color: '#637381' }}>
											Không có đơn phù hợp với bộ lọc.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</Box>
				)}
			</Paper>
		</Box>
	)
}
